import { Component, OnInit } from '@angular/core';
import { AuthService } from './services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import { distinctUntilChanged, filter, map, mergeMap } from 'rxjs/operators';
import { LocaleService } from './services/locale.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { UiBlockerComponent } from './modules/shared/ui-blocker/ui-blocker.component';
import { Toast } from 'primeng/toast';
import { StimulationCommentRestoreService } from './services/stimulation-comment-restore.service';
import { AppLayout } from './layout/components/app.layout';
import { PrimeNG } from 'primeng/config';
import { usesYearFirstDates } from './utils/date-format.util';
import {
  FormStyle,
  getLocaleDayNames,
  getLocaleMonthNames,
  TranslationWidth
} from '@angular/common';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],

  imports: [ConfirmDialog, UiBlockerComponent, RouterOutlet, Toast, AppLayout]
})
export class AppComponent implements OnInit {
  public leftModule: string | null = null;
  public rightModule: string | null = null;
  public showFullpage = false;

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private translate: TranslateService,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private localeService: LocaleService,
    private primeng: PrimeNG,
    private stimulationCommentRestoreService: StimulationCommentRestoreService
  ) {}
  /**
   * Localizes the PrimeNG calendar (day/month names, "Today"/"Clear" labels)
   * for the given UI language and forces the week to start on Monday.
   * Day/month names come from Angular's CLDR locale data (registered in
   * app.config.ts) rather than the ngx-translate files, since Angular already
   * ships correctly translated names for every supported locale.
   */
  private applyCalendarLocale(lang: string): void {
    // firstDayOfWeek/day/month names come from Angular's CLDR data and are
    // available synchronously - apply them right away so an inline calendar
    // mounting before the translation file loads (e.g. on a hard reload)
    // never sees PrimeNG's Sunday-first default. Only today/clear need the
    // async translateService.get() below, so they're patched in afterwards.
    this.primeng.setTranslation({
      // Every date picker takes its format from here (none sets its own):
      // day-first for the languages that write dates that way, year-first
      // (yyyy-MM-dd) for Lithuanian - see date-format.util.
      dateFormat: usesYearFirstDates(lang) ? 'yy-mm-dd' : 'dd.mm.yy',
      firstDayOfWeek: 1,
      dayNames: [
        ...getLocaleDayNames(lang, FormStyle.Standalone, TranslationWidth.Wide)
      ],
      dayNamesShort: [
        ...getLocaleDayNames(
          lang,
          FormStyle.Standalone,
          TranslationWidth.Abbreviated
        )
      ],
      dayNamesMin: [
        ...getLocaleDayNames(lang, FormStyle.Standalone, TranslationWidth.Short)
      ],
      monthNames: [
        ...getLocaleMonthNames(
          lang,
          FormStyle.Standalone,
          TranslationWidth.Wide
        )
      ],
      monthNamesShort: [
        ...getLocaleMonthNames(
          lang,
          FormStyle.Standalone,
          TranslationWidth.Abbreviated
        )
      ]
    });

    this.translateService
      .get(['common.today', 'common.clear'])
      .subscribe((translations: Record<string, string>) => {
        this.primeng.setTranslation({
          today: translations['common.today'],
          clear: translations['common.clear']
        });
      });
  }

  private findLastFirstChild(
    snapshot: ActivatedRouteSnapshot
  ): ActivatedRouteSnapshot {
    return snapshot.firstChild
      ? this.findLastFirstChild(snapshot.firstChild)
      : snapshot;
  }

  /**
   * Initialize current session language and locale.
   * This comes from the current user session or if user is not yet logged in
   * use default values.
   * The proper leftNavi value is also fetched from the router data
   */
  ngOnInit(): void {
    const user = this.authService.getUserProfile();
    const lang = user?.language || 'en';
    const locale = user?.activeOrganizationUnit?.country || 'en';
    this.localeService.initLocale(locale, lang);
    this.translateService.get('fetch-something').subscribe((result) => {});

    this.applyCalendarLocale(lang);
    this.translateService.onLangChange.subscribe((event) => {
      this.applyCalendarLocale(event.lang);
    });

    // ngOnInit only runs once at bootstrap, so on an interactive login
    // (SPA navigation, AppComponent never recreated) the profile transitions
    // from null to set well after this point - react to that transition via
    // the shared profile stream instead of only checking it once here.
    this.authService.userProfileObservable$
      .pipe(
        map((profile) => !!profile),
        distinctUntilChanged(),
        filter((loggedIn) => loggedIn)
      )
      .subscribe(() => {
        // Drain stimulation form comments that the original front-end-only
        // implementation left stranded in this browser's localStorage into
        // the database (no-op when the browser holds none).
        this.stimulationCommentRestoreService.sweepPendingComments();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activeRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data)
      )
      .subscribe((event) => {
        this.leftModule = event.leftNavi;
        if (event.leftNavi === 'login') {
          this.showFullpage = true;
        } else {
          this.showFullpage = false;
        }
      });
  }
}
